import { useCallback } from "react";
import {
  addRejectedFiles,
  clearValidation,
  nextStep,
  registerFiles,
  RejectionReason,
  useAppDispatch,
  type RejectedFile,
} from "../store";

const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB
const ALLOWED_TYPES = /^(image\/.*|video\/.*)$/;

export interface FileIntakeOptions {
  /** Advance to the review step after intake (used by the initial dropzone). */
  advance?: boolean;
}

const validateFiles = (files: FileList) => {
  const valid: File[] = [];
  const rejected: RejectedFile[] = [];

  Array.from(files).forEach((file) => {
    if (!ALLOWED_TYPES.test(file.type)) {
      rejected.push({ name: file.name, reason: RejectionReason.UNSUPPORTED_FORMAT });
    } else if (file.size >= MAX_FILE_SIZE) {
      rejected.push({ name: file.name, reason: RejectionReason.FILE_SIZE_TOO_BIG });
    } else {
      valid.push(file);
    }
  });

  return { valid, rejected };
};

/**
 * Shared file intake: validates a picked/dropped FileList, registers the
 * valid files (which the epic turns into thumbnails) and records any
 * rejections so they can be surfaced on the review step. Used by the initial
 * dropzone, which advances to review; from review, the add-more button sends
 * guests back to this dropzone to pick more (existing files are preserved).
 * Each call clears the previous validation result so only the latest pick's
 * errors are shown.
 */
export const useFileIntake = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (files: FileList, { advance = false }: FileIntakeOptions = {}) => {
      const { valid, rejected } = validateFiles(files);

      dispatch(clearValidation());
      if (valid.length > 0) dispatch(registerFiles(valid));
      if (rejected.length > 0) dispatch(addRejectedFiles(rejected));
      if (advance) dispatch(nextStep());
    },
    [dispatch],
  );
};
